import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { addToWatchlist, removeFromWatchlist } from '../redux/slices/userSlice';
import { setCurrentlyPlaying } from '../redux/slices/contentSlice';
import styled from 'styled-components';

const CardContainer = styled(motion.div)`
  position: relative;
  width: 180px;
  min-width: 180px;
  cursor: pointer;
  border-radius: 8px;
  overflow: visible;
  transition: transform 0.15s ease;

  &:hover {
    z-index: 9999;
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    width: 140px;
    min-width: 140px;
  }

  @media (max-width: 480px) {
    width: 110px;
    min-width: 110px;
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 270px;
  object-fit: cover;
  border-radius: 8px;
  transition: transform 0.15s ease;

  @media (max-width: 768px) {
    height: 210px;
  }

  @media (max-width: 480px) {
    height: 165px;
  }
`;

const HoverCard = styled(motion.div)`
  position: absolute;
  top: -40px;
  left: 0;
  right: 0;
  background: #181818;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.8);
  overflow: hidden;
  z-index: 10000;
  transform-origin: center bottom;
  min-width: 180px;

  @media (max-width: 768px) {
    min-width: 140px;
  }

  @media (max-width: 480px) {
    min-width: 110px;
  }
`;

const HoverImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;

  @media (max-width: 768px) {
    height: 150px;
  }

  @media (max-width: 480px) {
    height: 120px;
  }
`;

const HoverContent = styled.div`
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.8rem;
  }

  @media (max-width: 480px) {
    padding: 0.6rem;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.8rem;
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  background: transparent;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    border-color: white;
    background: rgba(255, 255, 255, 0.1);
  }

  &.play {
    background: white;
    color: black;
    border-color: white;

    &:hover {
      background: rgba(255, 255, 255, 0.8);
    }
  }

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }
`;

const MoreInfoButton = styled(Link)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  background: transparent;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  text-decoration: none;
  margin-left: auto;

  &:hover {
    border-color: white;
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }
`;

const Title = styled.h3`
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: white;
  line-height: 1.2;

  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-bottom: 0.3rem;
  }
`;

const SourceBadgeContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 5;

  @media (max-width: 480px) {
    top: 6px;
    right: 6px;
    gap: 3px;
  }
`;

const SourceBadge = styled.div`
  background: ${props => {
    switch(props.$source) {
      case 'WatchSeries': return 'rgba(229, 9, 20, 0.9)';
      case 'GoodMoviesHD': return 'rgba(46, 211, 105, 0.9)';
      case '0123Movie': return 'rgba(59, 130, 246, 0.9)';
      case 'MegaShareMovie': return 'rgba(168, 85, 247, 0.9)';
      case 'PlutoTV': return 'rgba(251, 146, 60, 0.9)';
      case 'TubiTV': return 'rgba(236, 72, 153, 0.9)';
      case 'Plex': return 'rgba(234, 179, 8, 0.9)';
      case 'RokuChannel': return 'rgba(20, 184, 166, 0.9)';
      case 'XumoPlay': return 'rgba(99, 102, 241, 0.9)';
      default: return 'rgba(239, 68, 68, 0.9)';
    }
  }};
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  backdrop-filter: blur(4px);
  text-align: center;

  @media (max-width: 480px) {
    font-size: 0.6rem;
    padding: 3px 6px;
  }
`;

const Metadata = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.7rem;
  color: #46d369;

  @media (max-width: 480px) {
    font-size: 0.65rem;
    margin-bottom: 0.3rem;
  }
`;

const Match = styled.span`
  color: #46d369;
  font-weight: 600;
`;

const IMDBRating = styled.span`
  color: #f5c518;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.2rem;

  &::before {
    content: '⭐';
    font-size: 0.8em;
  }
`;

const Rating = styled.span`
  border: 1px solid #808080;
  padding: 0.1rem 0.3rem;
  font-size: 0.6rem;
  color: #808080;

  @media (max-width: 480px) {
    font-size: 0.55rem;
    padding: 0.05rem 0.2rem;
  }
`;

const Duration = styled.span`
  color: #808080;
`;

const Genres = styled.div`
  font-size: 0.7rem;
  color: #808080;

  @media (max-width: 480px) {
    font-size: 0.65rem;
  }
`;

const ThumbnailCard = ({ content, index }) => {
  const dispatch = useDispatch();
  const { currentProfile } = useSelector(state => state.user);
  const [isHovered, setIsHovered] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);


  const isInWatchlist = currentProfile?.watchlist.includes(content.id);

  const handleMouseEnter = () => {
    const timeout = setTimeout(() => {
      setIsHovered(true);
    }, 200);
    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setIsHovered(false);
  };

  const handlePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Redirect to external watch page instead of local video player
    if (content.watchseriesUrl) {
      window.open(content.watchseriesUrl, '_blank');
    } else {
      // Fallback to local player if no external URL available
      dispatch(setCurrentlyPlaying(content));
    }
  };

  const handleWatchlistToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isInWatchlist) {
      dispatch(removeFromWatchlist(content.id));
    } else {
      dispatch(addToWatchlist(content.id));
    }
  };

  return (
    <CardContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: index * 0.05 }}
      tabIndex={0}
      role="button"
      aria-label={`${content.title} - ${content.type === 'tv' ? 'TV Show' : 'Movie'}`}
    >
      <ThumbnailImage
        src={content.thumbnailUrl}
        alt={content.title}
        loading="lazy"
      />

      {/* Display badges for all sources */}
      {content.sources && content.sources.length > 0 ? (
        <SourceBadgeContainer>
          {content.sources.map((src, idx) => (
            <SourceBadge key={idx} $source={src.source}>
              {src.source === 'WatchSeries' ? 'WS' :
               src.source === 'GoodMoviesHD' ? 'GM' :
               src.source === '0123Movie' ? '123' :
               src.source === 'MegaShareMovie' ? 'MS' :
               src.source === 'PlutoTV' ? 'PL' :
               src.source === 'TubiTV' ? 'TB' :
               src.source === 'Plex' ? 'PX' :
               src.source === 'RokuChannel' ? 'RK' :
               src.source === 'XumoPlay' ? 'XM' : src.source.substring(0, 3)}
            </SourceBadge>
          ))}
        </SourceBadgeContainer>
      ) : content.source && (
        <SourceBadgeContainer>
          <SourceBadge $source={content.source}>
            {content.source === 'WatchSeries' ? 'WS' :
             content.source === 'GoodMoviesHD' ? 'GM' :
             content.source === '0123Movie' ? '123' :
             content.source === 'MegaShareMovie' ? 'MS' :
             content.source === 'PlutoTV' ? 'PL' :
             content.source === 'TubiTV' ? 'TB' :
             content.source === 'Plex' ? 'PX' :
             content.source === 'RokuChannel' ? 'RK' :
             content.source === 'XumoPlay' ? 'XM' : content.source.substring(0, 3)}
          </SourceBadge>
        </SourceBadgeContainer>
      )}

      {isHovered && (
        <HoverCard
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.15 }}
        >
          <HoverImage
            src={content.thumbnailUrl}
            alt={content.title}
          />

          <HoverContent>
            <ButtonRow>
              <ActionButton
                className="play"
                onClick={handlePlay}
                aria-label={`Play ${content.title}`}
              >
                ▶
              </ActionButton>

              <ActionButton
                onClick={handleWatchlistToggle}
                aria-label={
                  isInWatchlist
                    ? `Remove ${content.title} from watchlist`
                    : `Add ${content.title} to watchlist`
                }
              >
                {isInWatchlist ? '✓' : '+'}
              </ActionButton>

              <ActionButton
                aria-label={`Like ${content.title}`}
              >
                👍
              </ActionButton>

              <MoreInfoButton
                to={`/title/${content.id}`}
                aria-label={`More info about ${content.title}`}
                onClick={(e) => e.stopPropagation()}
              >
                ⓘ
              </MoreInfoButton>
            </ButtonRow>

            <Title>{content.title}</Title>

            <Metadata>
              <Match>95% Match</Match>
              {content.imdbRating && (
                <IMDBRating>{content.imdbRating}</IMDBRating>
              )}
              {content.rating && content.rating !== 'Not Rated' && (
                <Rating>{content.rating}</Rating>
              )}
              <Duration>{content.duration}</Duration>
            </Metadata>

            <Genres>
              {content.genre.slice(0, 3).join(' • ')}
            </Genres>
          </HoverContent>
        </HoverCard>
      )}
    </CardContainer>
  );
};

export default ThumbnailCard;